import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { TAXONOMY } from '../constants';
import { closeOtpModal, setApprovalToken } from '../../../../redux/d2cAllocationSlice';
import {
  useRequestAllocationOtpMutation,
  useVerifyAllocationOtpMutation,
} from '../../../../redux/allocationApiSlice';

export default function OtpApprovalModal({ onVerified }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const otpModal = useSelector((s) => s.d2cAllocation.otpModal) || { open: false, title: '', body: '', purpose: null };
  const platinumEmail = useSelector((s) => s.d2cAllocation.platinumOtpEmail);
  const activeMerchantId = useSelector((s) => s.d2cAllocation.activeMerchantId);
  const [code, setCode] = useState('');
  const [otpId, setOtpId] = useState(null);

  const [requestAllocationOtp, { isLoading: isRequesting }] = useRequestAllocationOtpMutation();
  const [verifyAllocationOtp, { isLoading: isVerifying }] = useVerifyAllocationOtpMutation();

  // Request OTP as soon as the modal opens.
  useEffect(() => {
    if (otpModal.open && activeMerchantId) {
      setCode('');
      setOtpId(null);
      requestAllocationOtp({ merchantId: activeMerchantId, purpose: otpModal.purpose })
        .unwrap()
        .then((res) => {
          const result = res?.data ?? res;
          if (result?.otp_id) setOtpId(result.otp_id);
        })
        .catch(() => {
          enqueueSnackbar('Failed to send OTP. Please try again.', { variant: 'error' });
        });
    }
    if (!otpModal.open) {
      setCode('');
      setOtpId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpModal.open, activeMerchantId]);

  const handleVerify = async () => {
    if (!/^[0-9]{6}$/.test(code)) {
      enqueueSnackbar('Enter a 6-digit OTP', { variant: 'warning' });
      return;
    }
    try {
      const res = await verifyAllocationOtp({
        otp_id: otpId,
        merchantId: activeMerchantId,
        code,
        purpose: otpModal.purpose,
      }).unwrap();
      const result = res?.data ?? res;
      if (result?.verified) {
        if (result?.approvalToken) {
          dispatch(setApprovalToken(result.approvalToken));
        }
        dispatch(closeOtpModal());
        enqueueSnackbar('OTP verified — proceeding', { variant: 'success' });
        onVerified?.({ token: result.approvalToken, purpose: otpModal.purpose });
      } else {
        enqueueSnackbar(result?.error || 'OTP verification failed. Please try again.', { variant: 'error' });
      }
    } catch (err) {
      const msg = err?.data?.error || err?.data?.message || 'OTP verification failed.';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleClose = (_e, reason) => {
    // Don't let a backdrop tap interrupt an in-flight verify, but always allow an
    // explicit Cancel / Escape so the user is never trapped in the dialog.
    if (isVerifying && reason === 'backdropClick') return;
    dispatch(closeOtpModal());
  };

  return (
    <Dialog open={otpModal.open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VerifiedUserIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {otpModal.title || 'OTP required'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {otpModal.body ? (
          <Box sx={{ mb: 2 }} dangerouslySetInnerHTML={{ __html: otpModal.body }} />
        ) : null}
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Enter the 6-digit OTP sent to{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {platinumEmail || '— (no email registered)'}
          </Box>
          .
        </Typography>
        <TextField
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
          placeholder="••••••"
          inputProps={{
            inputMode: 'numeric',
            maxLength: 6,
            style: { fontSize: 22, letterSpacing: 8, fontFamily: 'monospace', textAlign: 'center' },
          }}
          fullWidth
          sx={{ mb: 1.5 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => dispatch(closeOtpModal())} variant="outlined" color="inherit" disabled={isVerifying}>
          Cancel
        </Button>
        <Button onClick={handleVerify} variant="contained" color="primary" disabled={isRequesting || isVerifying}>
          {isVerifying ? 'Verifying…' : 'Verify & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
